/* eslint-disable react/no-unescaped-entities */
import { LegalShell } from "./LegalShell";

export default function Privacy() {
  return (
    <LegalShell title="Privacy Policy" updated="February 18, 2026">
      <p>
        Welcome to <strong>b→come</strong> ("we", "us", "our"). We take your privacy seriously.
        This Privacy Policy explains what personal information we collect, how we use it, who we share it with,
        and the choices you have. By using b→come you agree to the practices described below.
      </p>

      <h2>1. Information we collect</h2>
      <p>We collect only what we need to give you a personalized AI life-coaching experience.</p>
      <ul>
        <li><strong>Account details</strong> — your name, email address, and (if you sign up with a password) a securely hashed password. If you sign in with Google or Facebook we receive your name, email, and profile picture from that provider only.</li>
        <li><strong>Goals &amp; progress</strong> — the goals, habits, milestones, tasks, journal entries, check-ins and vision-board items you create in the app.</li>
        <li><strong>Coaching context</strong> — messages you send to your AI coach and preferences you set (coaching mode, focus areas, coach memory).</li>
        <li><strong>Uploaded media</strong> — images you upload to your vision board and short sports-form videos you submit for feedback. These are stored on our servers and only accessible via unguessable URLs generated for your account.</li>
        <li><strong>Usage &amp; device data</strong> — the pages you visit inside the app, feature interactions, device type, browser, IP address and approximate location (derived from your IP). Used for analytics, abuse prevention and improving the product.</li>
        <li><strong>Payment data</strong> — billing is handled by <strong>Stripe</strong>. We never see or store your card number; we only store your subscription plan and Stripe customer/subscription identifiers.</li>
      </ul>

      <h2>2. How we use your information</h2>
      <ul>
        <li>Provide the core service: generate personalized plans, coaching messages, insights, and progress reports.</li>
        <li>Send you transactional emails (magic sign-in links, trial reminders, weekly review nudges, billing receipts).</li>
        <li>Send optional product updates and launch announcements (you can opt out at any time).</li>
        <li>Detect and prevent abuse, fraud, and violations of our Terms of Service.</li>
        <li>Comply with legal obligations.</li>
      </ul>

      <h2>3. AI processing</h2>
      <p>
        To power your coaching experience we send the minimum context needed to third-party AI providers —
        currently <strong>OpenAI</strong>, <strong>Anthropic</strong>, and <strong>Google (Gemini)</strong>.
        These providers process your data on our behalf under their own terms and do not train their base
        models on it. We never send your password, payment details, or precise location to any AI provider.
      </p>

      <h2>4. Who we share data with</h2>
      <p>We do <strong>not</strong> sell your personal information. We share only with service providers who help us run b→come:</p>
      <ul>
        <li><strong>Hosting &amp; database</strong> — our cloud infrastructure providers.</li>
        <li><strong>AI providers</strong> — OpenAI, Anthropic, Google Gemini (see Section 3).</li>
        <li><strong>Email delivery</strong> — Resend, for transactional and launch emails.</li>
        <li><strong>Payments</strong> — Stripe, for subscription billing.</li>
        <li><strong>Optional integrations you connect</strong> — Google Calendar, Facebook, Plaid, etc. Data flows only when you explicitly connect them.</li>
      </ul>
      <p>We may also disclose information if required by law, subpoena, or to protect the rights, property or safety of b→come, our users, or the public.</p>

      <h2>5. Data retention</h2>
      <p>
        We keep your account data for as long as your account is active. If you delete your account, we
        remove your personal data within 30 days, except where a longer retention period is required by
        law (for example, tax and billing records for up to 7 years).
      </p>

      <h2>6. Your rights &amp; controls</h2>
      <ul>
        <li><strong>Access &amp; export</strong> — you can view all of your data inside the app. Email us for a full export.</li>
        <li><strong>Correction</strong> — edit your profile, goals, and journal entries at any time in Settings.</li>
        <li><strong>Deletion</strong> — delete individual items from the app, or email <a href="mailto:hello@b-come.ai">hello@b-come.ai</a> to delete your entire account.</li>
        <li><strong>Marketing opt-out</strong> — every marketing email has an unsubscribe link at the bottom.</li>
        <li><strong>Disconnect integrations</strong> — revoke Google/Facebook/Plaid/Calendar access from Settings at any time.</li>
      </ul>
      <p>
        If you are in the EU/UK, you also have rights under the GDPR/UK GDPR including the right to
        object, restrict processing, and lodge a complaint with your local supervisory authority.
        Residents of California have rights under the CCPA to know, delete, and opt out of sale of
        personal information (we do not sell your data).
      </p>

      <h2>7. Security</h2>
      <p>
        Passwords are hashed with bcrypt. Sessions use short-lived signed tokens delivered via
        httpOnly cookies. All traffic is served over HTTPS. Payment card data never touches our servers.
        No system is perfectly secure, but we work hard to protect your information.
      </p>

      <h2>8. Children</h2>
      <p>
        b→come is not directed at children under 13. Our optional <strong>Kids Edition</strong> is designed
        to be used by children only under the active supervision of a parent or guardian who owns the
        account. Parents control every child profile, chore, allowance transaction, and reading log —
        children never sign in independently.
      </p>

      <h2>9. International transfers</h2>
      <p>
        We are based in the United States and our service providers may process your data in the US and
        other countries. Where required by law we use appropriate safeguards (such as EU Standard
        Contractual Clauses) for these transfers.
      </p>

      <h2>10. Cookies</h2>
      <p>
        We use a small number of first-party cookies for authentication (your login session) and to
        remember your preferences. We do not use third-party advertising cookies. You can clear cookies
        at any time from your browser settings; doing so will sign you out of b→come.
      </p>

      <h2>11. Changes to this policy</h2>
      <p>
        We may update this Privacy Policy from time to time. When we do, we will update the "Last updated"
        date at the top and, if the changes are material, notify you by email or an in-app notice before
        the changes take effect.
      </p>

      <h2>12. Contact us</h2>
      <p>
        Questions or requests about your data? Email us at{" "}
        <a href="mailto:hello@b-come.ai">hello@b-come.ai</a>. We aim to respond within 5 business days.
      </p>
    </LegalShell>
  );
}
